import type { Row } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AVATAR_MEDIA_TYPES,
  isAvatarMediaType,
} from "../../../../shared/avatarPolicy";
import { sharedMutators } from "../../../../shared/mutators";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { uploadFile } from "../../../lib/attachments";
import { cropImageToSquare } from "../../../lib/imageProcessing";
import { playClick } from "../../../lib/sounds";
import { attachmentUploadPath, userAvatarPath } from "../../../lib/apiPaths";
import { zIndexes } from "../../../lib/zIndexes";
import { Avatar } from "../../Avatar";
import { Icon } from "../../Icon";
import { PressButton } from "../../PressButton";
import { Text } from "../../Text";
import { useToast } from "../../Toast";

const AVATAR_SIZE = 64;
const AVATAR_DISPLAY_SIZE = 512;

const Trigger = styled("button")`
  position: relative;
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border: 0;
  padding: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: block;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const Overlay = styled("div")`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--motion-fast) var(--ease-out);
  pointer-events: none;
  ${Trigger}:hover &, ${Trigger}:focus-visible & {
    opacity: 1;
  }
`;

const Preview = styled("img")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MenuItem = styled(PressButton)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: transparent;
  color: var(--text-primary);
  text-align: left;
  &:hover {
    background: var(--alpha4);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export function AvatarUploader({
  user,
  size = AVATAR_SIZE,
}: {
  user: Row["user"];
  size?: number;
}) {
  const z = useZero();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    portalTarget: Element;
  } | null>(null);
  const [containerRef, menuRef] = useMouseDownOutside(() => setMenu(null));

  const openMenu = () => {
    playClick();
    if (!user.customAvatarKey) {
      inputRef.current?.click();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenu({
      x: rect.left,
      y: rect.bottom + 6,
      portalTarget: containerRef.current?.closest("dialog") ?? document.body,
    });
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!isAvatarMediaType(file.type)) {
      showToast({
        icon: "circle-off",
        message: "Use a PNG, JPEG, or WebP image.",
        buttonLabel: "Dismiss",
        variant: "error",
      });
      return;
    }
    setBusy(true);
    let nextPreviewUrl: string | null = null;
    try {
      const { blob } = await cropImageToSquare(file, {
        size: AVATAR_DISPLAY_SIZE,
        mediaType: file.type,
        quality: 0.92,
      });
      nextPreviewUrl = URL.createObjectURL(blob);
      setPreviewUrl(nextPreviewUrl);
      // WebKit cannot encode WebP and quietly hands back PNG, so trust the blob.
      const mediaType = isAvatarMediaType(blob.type) ? blob.type : "image/png";
      const key = await uploadFile(attachmentUploadPath(), blob, mediaType);
      await z.mutate(sharedMutators.setCustomAvatar({ key })).server;
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = userAvatarPath(key);
      });
    } catch (err) {
      showToast({
        icon: "circle-off",
        message: err instanceof Error ? err.message : "Couldn't upload avatar",
        buttonLabel: "Dismiss",
        variant: "error",
      });
    }
    setBusy(false);
    setPreviewUrl(null);
    if (nextPreviewUrl) URL.revokeObjectURL(nextPreviewUrl);
  };

  const onRemove = () => {
    z.mutate(sharedMutators.removeCustomAvatar({}));
  };

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <Trigger
        ref={triggerRef}
        type="button"
        style={{ width: size, height: size }}
        disabled={busy}
        onMouseDown={(e) => {
          e.preventDefault();
          if (menu) setMenu(null);
          else openMenu();
        }}
        aria-label="Edit profile photo"
      >
        {previewUrl ? (
          <Preview src={previewUrl} alt="" />
        ) : (
          <Avatar user={user} size={size} tooltip={false} />
        )}
        <Overlay>
          <Icon name="pencil" size={12} />
        </Overlay>
      </Trigger>
      <input
        ref={inputRef}
        type="file"
        accept={AVATAR_MEDIA_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={onChange}
      />
      {menu &&
        createPortal(
          <Menu
            ref={menuRef}
            data-popover
            data-direction="down"
            style={{ left: menu.x, top: menu.y }}
          >
            <MenuItem
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => {
                setMenu(null);
                inputRef.current?.click();
              }}
            >
              <Icon name="image-plus" size={16} />
              <Text variant="text-regular">
                {user.customAvatarKey ? "Change photo" : "Upload photo"}
              </Text>
            </MenuItem>
            {user.customAvatarKey && (
              <MenuItem
                onMouseDown={(e) => e.preventDefault()}
                onPress={() => {
                  setMenu(null);
                  onRemove();
                }}
              >
                <Icon name="trash" size={16} />
                <Text variant="text-regular">Remove photo</Text>
              </MenuItem>
            )}
          </Menu>,
          menu.portalTarget
        )}
    </div>
  );
}
